<template class="mr-3">
  <div class="card">
    <div class="card-body">
      <!-- Statistics Cards -->
      <a-row :gutter="16" class="mb-4">
        <a-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="stat-card total-payments">
            <div class="stat-icon">
              <a-icon type="file-text" />
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t("paiement.totalPaiements") }}</div>
              <div class="stat-value">{{ statistics.totalPayments }}</div>
            </div>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="stat-card total-amount">
            <div class="stat-icon">
              <a-icon type="dollar" />
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t("paiement.totalMontant") }}</div>
              <div class="stat-value">{{ formatCurrency(statistics.totalAmount) }}</div>
            </div>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="stat-card total-loans">
            <div class="stat-icon">
              <a-icon type="credit-card" />
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t("etatPaiement.totalAvances") }}</div>
              <div class="stat-value">{{ formatCurrency(statistics.totalLoans) }}</div>
            </div>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="stat-card total-employees">
            <div class="stat-icon">
              <a-icon type="team" />
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t("personnel.nbPersonnel") }}</div>
              <div class="stat-value">{{ statistics.totalEmployees }}</div>
            </div>
          </div>
        </a-col>
      </a-row>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-header">
          <h4>
            <a-icon type="filter" class="filter-icon" />
            {{ $t("all.filtresEtRecherche") }}
          </h4>
          <div class="action-button-group">
            <a-button
              type="default"
              @click="generatePdf"
              class="action-button"
            >
              <a-icon type="printer" class="action-icon" />
              {{ $t("action.imprimerTab") }}
            </a-button>
            <a-button
              type="primary"
              @click="showModal"
              class="action-button"
            >
              <a-icon type="plus" class="action-icon" />
              {{ $t("etatPaiement.paiementAction") }}
            </a-button>
          </div>
        </div>

        <a-row :gutter="[16, 16]" class="align-items-end">
          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="calendar" class="action-icon" />
                {{ $t("paiement.selectionnerMois") }}
              </label>
          <a-month-picker
                :value="selectedMonth"
                :placeholder="$t('paiement.selectionnerMois')"
                class="filter-select"
            format="MM-YYYY"
            @change="handleChangeMois"
          />
        </div>
          </a-col>

          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="credit-card" class="action-icon" />
                {{ $t("hr.modePaiement") }}
              </label>
              <a-select
                v-model="selectedPaymentMethod"
                :placeholder="$t('hr.modePaiement')"
                class="filter-select"
                @change="handlePaymentMethodFilter"
                mode="multiple"
                allowClear
              >
                <a-select-option value="ESPESE">{{
                  $t("hr.espece")
                }}</a-select-option>
                <a-select-option value="CHEQUE">{{
                  $t("hr.cheque")
                }}</a-select-option>
                <a-select-option value="VIREMENT">{{
                  $t("etatPaiement.virementBancaire")
                }}</a-select-option>
              </a-select>
            </div>
          </a-col>

          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="search" class="action-icon" />
                {{ $t("personnel.chercherParNom") }}
              </label>
              <a-input
                v-model="searchTerm"
                :placeholder="$t('personnel.chercherParNom')"
                class="filter-select"
                allow-clear
                @change="handleSearchTerm"
              >
                <a-icon slot="prefix" type="search" />
              </a-input>
            </div>
          </a-col>
        </a-row>
      </div>

      <!-- Table Section -->
      <div class="table-container">
        <a-table
          :pagination="{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${total} ${$t('paiement.lignes')}`,
          }"
          :data-source="data"
          :columns="columns"
          :loading="tableLoading"
          :scroll="{ x: 'max-content', y: false }"
          row-key="_id"
        >
          <template slot="month" slot-scope="text">{{
            moment(text).format("MMMM YYYY")
          }}</template>
          <template slot="paymentMethod" slot-scope="text, record">
          <a-tag
            :color="
              record.paymentMethod === 'VIREMENT'
                ? 'blue'
                : record.paymentMethod === 'CHEQUE'
                ? 'orange'
                : 'green'
            "
          >
            <a-icon
              :type="
                record.paymentMethod === 'VIREMENT'
                  ? 'bank'
                  : record.paymentMethod === 'CHEQUE'
                  ? 'file-text'
                  : 'dollar'
              "
              class="mr-1"
            />
            {{
              record.paymentMethod === "VIREMENT"
                ? $t("etatPaiement.virementBancaire")
                : record.paymentMethod === "CHEQUE"
                ? $t("hr.cheque")
                : $t("hr.espece")
            }}
          </a-tag>
        </template>
        <template slot="employee" slot-scope="text, record">
          <div>
            <strong>{{ record.employee.firstName }} {{ record.employee.lastName }}</strong>
            <div v-if="record.employee.cin" class="text-muted small">
              {{ $t('personnel.cin') }}: {{ record.employee.cin }}
            </div>
          </div>
        </template>
        <template slot="salary" slot-scope="text, record">
          <strong class="text-primary">{{ formatCurrency(parseFloat(text || 0)) }}</strong>
        </template>
        <template slot="loan" slot-scope="text, record">
          <span v-if="parseFloat(text || 0) > 0" class="text-warning">
            {{ formatCurrency(parseFloat(text || 0)) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template slot="createdAt" slot-scope="text, record">
          {{ moment(record.createdAt).format("DD/MM/YYYY HH:mm") }}
        </template>
        <template slot="paymentStatus" slot-scope="text, record">
          <a-tag
            :color="
              text === 'paid' || text === 'payé'
                ? 'green'
                : text === 'pending' || text === 'en attente'
                ? 'orange'
                : 'red'
            "
          >
            {{ translatePaymentStatus(text) }}
          </a-tag>
        </template>
        <div
          slot="filterDropdown"
          slot-scope="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('personnel.chercher')} ${column.title}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
            "
            @pressEnter="
              () => handleSearch(selectedKeys, confirm, column.dataIndex)
            "
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            style="width: 90px; margin-right: 8px"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >{{ $t("paiement.chercher") }}</a-button
          >
          <a-button
            size="small"
            style="width: 90px"
            @click="() => handleReset(clearFilters)"
            >{{ $t("action.reinitialiser") }}</a-button
          >
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <template slot="actions" slot-scope="text, record">
          <div class="editable-row-operations">
            <a-button
              type="primary"
              icon="edit"
              size="small"
              block
              style="margin-bottom: 4px"
              @click="editPayment(record)"
            >
              {{ $t("action.modifier") }}
        </a-button>
            <a-popconfirm
              :title="$t('all.sureToDelete')"
              @confirm="deletePayment(record._id)"
            >
              <a-button type="danger" icon="delete" size="small" block>
                {{ $t("action.supprimer") }}
              </a-button>
            </a-popconfirm>
          </div>
        </template>
        <template slot="customRender" slot-scope="text, record, index, column">
          <span v-if="searchText && searchedColumn === column.dataIndex">
            <template
              v-for="(fragment, i) in (text || '')
                .toString()
                .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
            >
              <mark
                v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                :key="i"
                class="highlight"
                >{{ fragment }}</mark
              >
              <template v-else>{{ fragment }}</template>
            </template>
          </span>
          <template v-else>{{ text || "-" }}</template>
        </template>
        </a-table>
      </div>

        <a-modal
          v-model="visibleModal"
          @cancel="handleCancel"
        :title="
          isEditMode
            ? $t('hr.modifierPaiement')
            : $t('all.enterInformationBelow')
        "
          :width="800"
          :footer="false"
        >
          <a-form :form="form" class="container" @submit="addPaiement">
            <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('etatPaiement.personnel')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    :placeholder="$t('etatPaiement.personnel')"
                    @change="changeSalary"
                    v-decorator="[
                      'employee',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.nomPersonnel'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      v-for="personnel in employeeList"
                      :key="personnel._id"
                      :value="personnel._id"
                    >
                      {{ personnel.firstName }} {{ personnel.lastName }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-lg-6">
                <a-form-item :label="$t('personnel.salaire')">
                  <a-input
                    :placeholder="$t('personnel.salaire')"
                    type="number"
                    step="0.1"
                    v-decorator="[
                      'salary',
                      {
                        setFieldsValue: salary,
                        rules: [
                          {
                            required: true,
                            message: $t('requis.salaire'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
          <!-- Employee Info Display -->
          <div v-if="selectedEmployeeInfo" class="row mb-3">
            <div class="col-lg-12">
              <a-alert
                type="info"
                :message="$t('personnel.infoPersonnel')"
                :show-icon="true"
                :closable="false"
              >
                <template slot="description">
                  <div class="employee-info-display">
                    <a-row :gutter="16">
                      <a-col :xs="24" :sm="12" :md="6">
                        <div class="info-item">
                          <strong>{{ $t("personnel.cin") }}:</strong>
                          <span>{{ selectedEmployeeInfo.cin || "-" }}</span>
                        </div>
                      </a-col>
                      <a-col :xs="24" :sm="12" :md="6">
                        <div class="info-item">
                          <strong>{{ $t("personnel.telephone") }}:</strong>
                          <span>{{ selectedEmployeeInfo.phone || "-" }}</span>
                        </div>
                      </a-col>
                      <a-col :xs="24" :sm="12" :md="6">
                        <div class="info-item">
                          <strong>{{ $t("personnel.typeContrat") }}:</strong>
                          <span>{{ selectedEmployeeInfo.contract || "-" }}</span>
                        </div>
                      </a-col>
                      <a-col :xs="24" :sm="12" :md="6">
                        <div class="info-item">
                          <strong>{{ $t("personnel.type") }}:</strong>
                          <a-tag :color="selectedEmployeeInfo.isA_Teacher ? 'blue' : 'green'">
                            {{ selectedEmployeeInfo.isA_Teacher ? $t("personnel.enseignant") : $t("personnel.personnel") }}
                          </a-tag>
                        </div>
                      </a-col>
                    </a-row>
                  </div>
                </template>
              </a-alert>
              </div>
            </div>
          <!-- Teacher attendance reference (read-only, no autofill) -->
          <div
            v-if="selectedEmployeeInfo && selectedEmployeeInfo.isA_Teacher"
            class="row mb-3"
          >
            <div class="col-lg-12">
              <a-spin :spinning="attendanceLoading">
                <div class="attendance-ref-card">
                  <div class="attendance-ref-header">
                    <div class="attendance-ref-title-wrap">
                      <div class="attendance-ref-icon">
                        <a-icon type="schedule" />
                      </div>
                      <div>
                        <div class="attendance-ref-title">
                          {{ $t("etatPaiement.attendanceReference") }}
                        </div>
                        <div class="attendance-ref-hint">
                          {{ $t("etatPaiement.attendanceReferenceHint") }}
                        </div>
                      </div>
                    </div>
                    <a-button
                      v-if="attendanceSummary"
                      type="primary"
                      ghost
                      size="small"
                      :loading="attendanceDetailsLoading"
                      @click="openAttendanceDetails"
                    >
                      <a-icon type="unordered-list" />
                      {{ $t("etatPaiement.attendanceDetails") }}
                    </a-button>
                  </div>

                  <div v-if="attendanceSummary" class="attendance-summary-grid">
                    <div class="att-stat">
                      <div class="att-label">{{ $t("assiduite.present") }}</div>
                      <div class="att-value">{{ attendanceSummary.present }}</div>
                    </div>
                    <div class="att-stat">
                      <div class="att-label">{{ $t("assiduite.absence") }}</div>
                      <div class="att-value">{{ attendanceSummary.absent }}</div>
                    </div>
                    <div class="att-stat">
                      <div class="att-label">{{ $t("assiduite.retard") }}</div>
                      <div class="att-value">{{ attendanceSummary.late }}</div>
                    </div>
                    <div class="att-stat">
                      <div class="att-label">{{ $t("teacherAttendance.delayMinutes") }}</div>
                      <div class="att-value">{{ attendanceSummary.delayMinutes }}</div>
                    </div>
                    <div class="att-stat">
                      <div class="att-label">{{ $t("teacherAttendance.workedDays") }}</div>
                      <div class="att-value">{{ attendanceSummary.workedDays }}</div>
                    </div>
                    <div class="att-stat">
                      <div class="att-label">{{ $t("teacherAttendance.workedHours") }}</div>
                      <div class="att-value">{{ attendanceSummary.workedHours }}</div>
                    </div>
                  </div>
                  <div v-else-if="!attendanceLoading" class="attendance-ref-empty">
                    {{ $t("etatPaiement.noAttendanceData") }}
                  </div>
                </div>
              </a-spin>
            </div>
          </div>
            <div class="row">
              <div class="col-lg-4">
              <a-form-item :label="$t('hr.nbJours')">
                  <a-input
                  :placeholder="$t('hr.nbJours')"
                    type="number"
                    v-decorator="[
                      'dayNbr',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.nbJours'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            <div class="col-lg-4">
              <a-form-item :label="$t('hr.nbHeures')">
                <a-input
                  :placeholder="$t('hr.nbHeures')"
                  type="number"
                  step="0.1"
                  v-decorator="[
                    'hourNbr',
                    {
                      rules: [],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
              <div class="col-lg-4">
                <a-form-item :label="$t('etatPaiement.avance')">
                  <a-input
                    :placeholder="$t('etatPaiement.avance')"
                    type="number"
                    step="0.1"
                    v-decorator="[
                      'loan',
                      {
                      rules: [],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
          </div>
          <div class="row">
            <div class="col-lg-6">
              <a-form-item :label="$t('hr.modePaiement')">
                <a-select
                    v-decorator="[
                    'paymentMethod',
                      {
                      initialValue: 'ESPESE',
                        rules: [
                          {
                            required: true,
                          message: $t('requis.modePaiement'),
                          },
                        ],
                      },
                    ]"
                >
                  <a-select-option value="ESPESE">{{
                    $t("hr.espece")
                  }}</a-select-option>
                  <a-select-option value="CHEQUE">{{
                    $t("hr.cheque")
                  }}</a-select-option>
                  <a-select-option value="VIREMENT">{{
                    $t("etatPaiement.virementBancaire")
                  }}</a-select-option>
                </a-select>
                </a-form-item>
              </div>
              <div class="col-lg-6">
              <a-form-item :label="$t('etatPaiement.mois')">
                  <a-month-picker
                    style="width: 100%"
                    @change="onPaymentMonthChange"
                    v-decorator="[
                      'month',
                      {
                        initialValue: moment(),
                        rules: [
                          {
                            required: true,
                            message: $t('requis.mois'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
          </div>
          <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('etatPaiement.etatPaiement')">
                  <a-input
                    :placeholder="$t('etatPaiement.etatPaiement')"
                    v-decorator="[
                      'paymentStatus',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.etatPaiement'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
          <div class="row">
            <div class="col-lg-12">
              <a-form-item :label="$t('paiement.note')">
                <a-textarea
                  :placeholder="$t('paiement.note')"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  v-decorator="[
                    'note',
                    {
                      rules: [],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
            <div class="form-actions mt-0">
              <a-button
                style="width: 150px"
                type="primary"
                htmlType="submit"
                :loading="loadingPaiement"
                class="mr-3"
              >
              {{
                isEditMode
                  ? $t("action.enregistrer")
                  : $t("action.ajouter")
              }}
              </a-button>
              <a-button
                key="back"
                @click="handleCancel"
                :disabled="loadingPaiement"
              >
                {{ $t("action.annuler") }}
              </a-button>
            </div>
          </a-form>
        </a-modal>

        <a-modal
          v-model="attendanceDetailsVisible"
          :title="$t('etatPaiement.attendanceDetailsTitle')"
          :width="900"
          :footer="null"
          :zIndex="1100"
          @cancel="attendanceDetailsVisible = false"
        >
          <div class="attendance-details-meta mb-3">
            <span v-if="selectedEmployeeInfo">
              <strong>{{ $t("etatPaiement.personnel") }}:</strong>
              {{ selectedEmployeeInfo.firstName }}
              {{ selectedEmployeeInfo.lastName }}
            </span>
            <span v-if="attendanceDetailsPeriod" class="ml-3">
              <strong>{{ $t("etatPaiement.mois") }}:</strong>
              {{ attendanceDetailsPeriod }}
            </span>
          </div>
          <a-spin :spinning="attendanceDetailsLoading">
            <a-table
              v-if="attendanceSessions.length"
              :columns="attendanceDetailsColumns"
              :data-source="attendanceSessions"
              :pagination="{ pageSize: 8, hideOnSinglePage: true }"
              row-key="id"
              size="middle"
              :scroll="{ x: true }"
            >
              <template slot="date" slot-scope="text">
                {{ text ? moment(text).format("DD/MM/YYYY") : "-" }}
              </template>
              <template slot="session" slot-scope="text, record">
                <div class="session-cell">
                  <div class="session-main">
                    {{ record.subjectName || "-" }}
                    <span v-if="record.className" class="session-class">
                      · {{ record.className }}
                    </span>
                  </div>
                  <div class="session-time text-muted">
                    <template v-if="record.time || record.ends">
                      {{ record.time || "?" }} – {{ record.ends || "?" }}
                    </template>
                    <template v-if="record.duration">
                      · {{ record.duration }} min
                    </template>
                  </div>
                </div>
              </template>
              <template slot="status" slot-scope="text, record">
                <a-tag :color="attendanceStatusColor(text)">
                  {{ $t(`teacherAttendance.status_${text}`) }}
                </a-tag>
                <a-tag v-if="record.flagOffSite" color="orange">
                  {{ $t("teacherAttendance.offSite") }}
                </a-tag>
              </template>
              <template slot="delay" slot-scope="text, record">
                {{
                  record.status === "late" && text
                    ? `${text} min`
                    : "-"
                }}
              </template>
              <template slot="source" slot-scope="text">
                {{ attendanceSourceLabel(text) }}
              </template>
            </a-table>
            <div
              v-else-if="!attendanceDetailsLoading"
              class="attendance-ref-empty"
            >
              {{ $t("etatPaiement.noAttendanceData") }}
            </div>
          </a-spin>
        </a-modal>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import moment from "moment";
import "jspdf-autotable";

import { mapState } from "vuex";
import apiClient from "@/services/axios";
import { pdfHeader } from "@/utilities/pdfUtils";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

const fileList = [
  {
    uid: "-1",
    name: "EdTrust",
    status: "done",
    url: "../../../resources/images/eleve.jpg",
  },
];

export default {
  created() {
    this.currency = getBuildingCurrency();
    apiClient
      .post("/employees/filter", {
        status: "active",
      })
      .then((res) => (this.employeeList = res.data))
      .catch((err) => {
        if (
          err.response &&
          err.response.data &&
          err.response.data.errorCode
        ) {
          this.$message.error(
            this.$t(`errorCode.${err.response.data.errorCode}`)
          );
        }
      });
    this.fetchEmployeePayments();
  },
  computed: {
    ...mapState(["settings"]),
    statistics() {
      const totalPayments = this.data.length;
      const totalAmount = this.data.reduce((sum, item) => {
        return sum + (parseFloat(item.salary) || 0);
      }, 0);
      const totalLoans = this.data.reduce((sum, item) => {
        return sum + (parseFloat(item.loan) || 0);
      }, 0);
      const uniqueEmployees = new Set(
        this.data.map((item) => item.employee?._id || item.employee)
      );
      return {
        totalPayments,
        totalAmount,
        totalLoans,
        totalEmployees: uniqueEmployees.size,
      };
    },
    attendanceDetailsColumns() {
      return [
        {
          title: this.$t("assiduite.date"),
          dataIndex: "date",
          key: "date",
          width: 110,
          scopedSlots: { customRender: "date" },
        },
        {
          title: this.$t("assiduite.seance"),
          key: "session",
          scopedSlots: { customRender: "session" },
        },
        {
          title: this.$t("assiduite.type"),
          dataIndex: "status",
          key: "status",
          width: 160,
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("teacherAttendance.delayMinutes"),
          dataIndex: "delayMinutes",
          key: "delay",
          width: 110,
          scopedSlots: { customRender: "delay" },
        },
        {
          title: this.$t("etatPaiement.attendanceSource"),
          dataIndex: "source",
          key: "source",
          width: 130,
          scopedSlots: { customRender: "source" },
        },
      ];
    },
    columns() {
      return [
        {
          title: this.$t("etatPaiement.personnel"),
          dataIndex: "employee",
          key: "employee",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "employee",
          },
          onFilter: (value, record) =>
            record.employee.toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("etatPaiement.salaire"),
          dataIndex: "salary",
          key: "salary",
          align: "right",
          scopedSlots: { customRender: "salary" },
        },
        {
          title: this.$t("etatPaiement.avance"),
          dataIndex: "loan",
          key: "loan",
          align: "right",
          scopedSlots: { customRender: "loan" },
        },
        {
          title: this.$t("etatPaiement.mois"),
          dataIndex: "month",
          key: "month",
          scopedSlots: {
            customRender: "month",
          },
        },
        {
          title: this.$t("etatPaiement.etatPaiement"),
          dataIndex: "paymentStatus",
          key: "paymentStatus",
          scopedSlots: { customRender: "paymentStatus" },
        },
        {
          title: this.$t("hr.modePaiement"),
          dataIndex: "paymentMethod",
          key: "paymentMethod",
          scopedSlots: {
            customRender: "paymentMethod",
          },
        },
        {
          title: this.$t("etablissement.dateCreation"),
          dataIndex: "createdAt",
          key: "createdAt",
          scopedSlots: { customRender: "createdAt" },
        },
        {
          title: this.$t("action.actions"),
          key: "actions",
          scopedSlots: { customRender: "actions" },
        },
      ];
    },
  },
  data() {
    return {
      editingKey: "",
      visibleParentFile: false,
      rowData: [],
      data: [],
      employeeList: [],
      form: this.$form.createForm(this),
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      searchTerm: "",
      selectedPaymentMethod: [],
      selectedMonth: null,
      targetKeys: [],
      previewImage: "",
      fileList,
      loadingPaiement: false,
      salary: 0,
      visibleModal: false,
      isEditMode: false,
      editingPaymentId: null,
      selectedEmployeeInfo: null,
      tableLoading: false,
      attendanceSummary: null,
      attendanceLoading: false,
      attendanceRequestId: 0,
      attendanceDetailsVisible: false,
      attendanceDetailsLoading: false,
      attendanceSessions: [],
      attendanceDetailsPeriod: "",
    };
  },
  methods: {
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    translatePaymentStatus(status) {
      if (!status) return '-';
      const statusLower = status.toLowerCase();
      if (statusLower === 'paid' || statusLower === 'payé') {
        return this.$t('activityPayment.paid');
      } else if (statusLower === 'pending' || statusLower === 'en attente') {
        return this.$t('all.pending');
      } else if (statusLower === 'unpaid' || statusLower === 'non payé') {
        return this.$t('activityPayment.unpaid');
      }
      return status;
    },
    fetchEmployeePayments() {
      this.tableLoading = true;
      return apiClient
        .get("/employee_payments")
        .then((res) => {
          this.data = res.data;
          this.rowData = this.data.map((item) => ({ ...item }));
          this.cacheData = this.data.map((item) => ({ ...item }));
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.errorCode
          ) {
            this.$message.error(
              this.$t(`errorCode.${err.response.data.errorCode}`)
            );
          } else {
            this.$message.error(this.$t("error.fetchingData"));
          }
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    changeSalary(val) {
      // Find employee in employeeList
      const employee = this.employeeList.find((emp) => emp._id === val);
      
      if (employee) {
        // Display employee info
        this.selectedEmployeeInfo = employee;
        
        // Set default values from employee data
          this.form.setFieldsValue({
          salary: employee.salary?.net ? parseFloat(employee.salary.net) : 0,
          dayNbr: employee.dayOfWeek ? parseFloat(employee.dayOfWeek) : 0,
          hourNbr: employee.hourlyRate ? parseFloat(employee.hourlyRate) : 0,
        });
        this.loadAttendanceSummary();
      } else {
        this.selectedEmployeeInfo = null;
        this.clearAttendanceSummary();
        this.$message.warning(this.$t("error.aucPersonnel"));
      }
    },
    clearAttendanceSummary() {
      this.attendanceSummary = null;
      this.attendanceLoading = false;
      this.attendanceSessions = [];
      this.attendanceDetailsPeriod = "";
      this.attendanceDetailsVisible = false;
    },
    onPaymentMonthChange() {
      this.$nextTick(() => {
        this.loadAttendanceSummary();
      });
    },
    attendanceStatusColor(status) {
      if (status === "present") return "green";
      if (status === "absent") return "red";
      if (status === "late") return "orange";
      return "blue";
    },
    attendanceSourceLabel(source) {
      if (source === "teacher_checkin") {
        return this.$t("etatPaiement.attendanceSourceCheckin");
      }
      if (source === "admin_manual") {
        return this.$t("etatPaiement.attendanceSourceManual");
      }
      return source || "-";
    },
    async openAttendanceDetails() {
      const employee = this.selectedEmployeeInfo;
      if (!employee || !employee.isA_Teacher) return;

      const monthValue = this.form.getFieldValue("month") || moment();
      const startDate = moment(monthValue).startOf("month").format("YYYY-MM-DD");
      const endDate = moment(monthValue).endOf("month").format("YYYY-MM-DD");
      this.attendanceDetailsPeriod = moment(monthValue).format("MMMM YYYY");
      this.attendanceDetailsVisible = true;
      this.attendanceDetailsLoading = true;
      this.attendanceSessions = [];

      try {
        const { data } = await apiClient.post("/teacherAttendance/summary", {
          startDate,
          endDate,
          employee: employee._id,
          includeDetails: true,
        });
        this.attendanceSessions = data.sessions || [];
      } catch (err) {
        this.attendanceSessions = [];
        if (
          err.response &&
          err.response.data &&
          err.response.data.errorCode
        ) {
          this.$message.error(
            this.$t(`errorCode.${err.response.data.errorCode}`)
          );
        } else {
          this.$message.error(this.$t("error.fetchingData"));
        }
      } finally {
        this.attendanceDetailsLoading = false;
      }
    },
    async loadAttendanceSummary() {
      const employee = this.selectedEmployeeInfo;
      if (!employee || !employee.isA_Teacher) {
        this.clearAttendanceSummary();
        return;
      }

      const monthValue = this.form.getFieldValue("month") || moment();
      const startDate = moment(monthValue).startOf("month").format("YYYY-MM-DD");
      const endDate = moment(monthValue).endOf("month").format("YYYY-MM-DD");
      const requestId = ++this.attendanceRequestId;

      this.attendanceLoading = true;
      this.attendanceSummary = null;
      try {
        const { data } = await apiClient.post("/teacherAttendance/summary", {
          startDate,
          endDate,
          employee: employee._id,
        });
        if (requestId !== this.attendanceRequestId) return;
        const row = data.teachers && data.teachers[0];
        this.attendanceSummary = row
          ? {
              present: row.present || 0,
              absent: row.absent || 0,
              late: row.late || 0,
              delayMinutes: row.delayMinutes || 0,
              workedDays: row.workedDays || 0,
              workedHours: row.workedHours || 0,
            }
          : null;
      } catch (err) {
        if (requestId !== this.attendanceRequestId) return;
        this.attendanceSummary = null;
        if (
          err.response &&
          err.response.data &&
          err.response.data.errorCode
        ) {
          this.$message.error(
            this.$t(`errorCode.${err.response.data.errorCode}`)
          );
        }
      } finally {
        if (requestId === this.attendanceRequestId) {
          this.attendanceLoading = false;
        }
      }
    },
    moment,
    handleChange(value, key, column) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      if (target) {
        target[column] = value;
        this.data = newData;
      }
    },
    edit(key) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      this.editingKey = key;
      if (target) {
        target.editable = true;
        this.data = newData;
      }
    },
    save(key) {
      const newData = [...this.data];
      const newCacheData = [...this.cacheData];
      const target = newData.filter((item) => key === item.key)[0];
      const targetCache = newCacheData.filter((item) => key === item.key)[0];
      if (target && targetCache) {
        delete target.editable;
        this.data = newData;
        Object.assign(targetCache, target);
        this.cacheData = newCacheData;
      }
      this.editingKey = "";
    },
    cancel(key) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      this.editingKey = "";
      if (target) {
        Object.assign(
          target,
          this.cacheData.filter((item) => key === item.key)[0]
        );
        delete target.editable;
        this.data = newData;
      }
    },
    onDelete(key) {
      const dataSource = [...this.data];
      this.data = dataSource.filter((item) => item.key !== key);
    },
    addPaiement(e) {
      e.preventDefault();
      this.form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          // Prepare data - convert month to Date if it's a moment object
          const data = {
            ...values,
            month: values.month ? moment(values.month).toDate() : new Date(),
            loan: values.loan || 0,
            hourNbr: values.hourNbr || null,
            note: values.note || null,
          };
          this.loadingPaiement = true;

          const apiCall = this.isEditMode
            ? apiClient.patch(`/employee_payments/${this.editingPaymentId}`, {
                data,
              })
            : apiClient.put("/employee_payments", { data });

          apiCall
            .then((res) => {
              if (this.isEditMode) {
                this.$message.success(this.$t("hr.paiementModifie"));
              } else {
                this.$message.success(this.$t("success.paiementAjout"));
              }
            this.visibleModal = false;
            this.form.resetFields();
              this.isEditMode = false;
              this.editingPaymentId = null;
              this.selectedEmployeeInfo = null;
              this.clearAttendanceSummary();
              // Reload data
              this.fetchEmployeePayments();
            })
            .catch((err) => {
              if (
                err.response &&
                err.response.data &&
                err.response.data.errorCode
              ) {
                this.$message.error(
                  this.$t(`errorCode.${err.response.data.errorCode}`)
                );
              } else {
                this.$message.error(
                   this.$t("error.add")
                );
              }
            })
            .finally(() => {
              this.loadingPaiement = false;
          });
        } else this.$message.warning(this.$t("warning.verifieDonnees"));
      });
    },
    handleCancel(e) {
      this.visibleModal = false;
      this.isEditMode = false;
      this.editingPaymentId = null;
      this.selectedEmployeeInfo = null;
      this.clearAttendanceSummary();
      this.form.resetFields();
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },
    getMock() {
      const targetKeys = [];
      const mockData = [];
      for (let i = 0; i < 20; i++) {
        const data = {
          key: i.toString(),
          title: `content${i + 1}`,
          description: `description of content${i + 1}`,
          chosen: Math.random() * 2 > 1,
        };
        if (data.chosen) {
          targetKeys.push(data.key);
        }
        mockData.push(data);
      }
      this.mockData = mockData;
      this.targetKeys = targetKeys;
    },
    resetFilter() {
      this.data = this.cacheData.map((item) => ({ ...item }));
      this.searchTerm = "";
      this.selectedPaymentMethod = [];
      this.selectedMonth = null;
      this.applyFilters();
    },
    handleSearchTerm() {
      this.applyFilters();
    },
    handlePaymentMethodFilter(value) {
      this.selectedPaymentMethod = value;
      this.applyFilters();
    },
    handleChangeMois(value) {
      this.selectedMonth = value;
      this.applyFilters();
    },
    applyFilters() {
      let filteredData = this.cacheData.map((item) => ({ ...item }));

      // Apply payment method filter
      if (
        this.selectedPaymentMethod &&
        this.selectedPaymentMethod.length > 0
      ) {
        filteredData = filteredData.filter((record) => {
          return this.selectedPaymentMethod.includes(
            record.paymentMethod || "ESPESE"
          );
        });
      }

      // Apply month filter
      if (this.selectedMonth) {
        const selectedYear = moment(this.selectedMonth).year();
        const selectedMonthNum = moment(this.selectedMonth).month();
        filteredData = filteredData.filter((record) => {
          const recordDate = moment(record.month);
          return (
            recordDate.year() === selectedYear &&
            recordDate.month() === selectedMonthNum
          );
        });
      }

      // Apply search term filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filteredData = filteredData.filter((item) => {
          const employee = item.employee;
          const name = employee
            ? `${employee.firstName || ""} ${employee.lastName || ""}`.toLowerCase()
            : "";
          return name.includes(searchLower);
        });
      }

      this.data = filteredData;
      this.rowData = filteredData;
    },
    generatePdf() {
      this.$gtag.event("Imp etatDePaiement personnels", {
        event_category: "Impression PDF",
        event_label: "Personnel - etatDePaiement section",
        value: 1,
      });

      const isArabic = this.$i18n.locale === 'ar-AR';
      
      // Helper function to reverse array if Arabic
      const reverseIfArabic = (arr) => isArabic ? arr.slice().reverse() : arr;
      
      // Use pdfHeader utility function
      const doc = pdfHeader(this.settings, {
        title: this.$t("etatPaiement.paiementsPersonnels").toUpperCase(),
        locale: this.$i18n.locale
      });
      
      var arr = [];
      let counter = 1;
      let count = 0;
      let reste = 0;
      let sommeReste = 0;
      let sommeAvance = 0;
      const array = [];
      for (let i = 0; i < this.data.length; i++) {
        arr.push(counter);
        arr.push(
          this.data[i].employee.firstName + " " + this.data[i].employee.lastName
        );
        arr.push(this.data[i].employee.cin);
        arr.push(this.data[i].salary);
        const loanAmount = this.data[i].loan || 0;
        arr.push(loanAmount);
        sommeAvance += loanAmount;
        reste = parseFloat(this.data[i].salary) - loanAmount;
        arr.push(reste);
        sommeReste += reste;
        
        // Reverse for Arabic if needed
        if (isArabic) {
          arr.reverse();
        }
        
        array.push(arr);
        arr = [];
        count += parseFloat(this.data[i].salary);
        counter++;
      }

      doc.autoTable({
        theme: "grid",
        styles: { font: "Amiri", halign: isArabic ? "right" : "left" },
        headStyles: { halign: isArabic ? "right" : "left" },
        bodyStyles: { halign: isArabic ? "right" : "left" },
        startY: 45,
        head: [
          reverseIfArabic([
            this.$t("inscription.num"),
            this.$t("inscription.nom"),
            this.$t("personnel.cin"),
            this.$t("personnel.salaire"),
            this.$t("etatPaiement.avance"),
            this.$t("etatPaiement.reste"),
          ])
        ],
        body: array,
        foot: [
          reverseIfArabic([
            {
              colSpan: 3,
              content: this.$t("liste.total") + " :",
              styles: { halign: "center" },
            },
            parseFloat(count),
            parseFloat(sommeAvance),
            parseFloat(sommeReste),
          ]),
        ],
      });

      doc.save(this.$t("etatPaiement.paiementsPersonnels") + ".pdf");
    },
    showModal() {
      this.isEditMode = false;
      this.editingPaymentId = null;
      this.selectedEmployeeInfo = null;
      this.clearAttendanceSummary();
      this.visibleModal = true;
      this.form.resetFields();
      // Set default month to current month for new payments
      this.$nextTick(() => {
        this.form.setFieldsValue({
          month: moment(),
        });
      });
    },
    editPayment(record) {
      this.isEditMode = true;
      this.editingPaymentId = record._id;
      this.visibleModal = true;
      
      // Find and set employee info
      const employeeId = record.employee._id || record.employee;
      const employee = this.employeeList.find((emp) => emp._id === employeeId);
      if (employee) {
        this.selectedEmployeeInfo = employee;
      } else {
        this.selectedEmployeeInfo = null;
        this.clearAttendanceSummary();
      }
      
      // Set form values
      this.$nextTick(() => {
        this.form.setFieldsValue({
          employee: employeeId,
          salary: record.salary,
          dayNbr: record.dayNbr,
          hourNbr: record.hourNbr,
          loan: record.loan || 0,
          paymentStatus: record.paymentStatus,
          month: moment(record.month),
          paymentMethod: record.paymentMethod || "ESPESE",
          note: record.note || "",
        });
        this.loadAttendanceSummary();
      });
    },
    deletePayment(id) {
      apiClient
        .delete(`/employee_payments/${id}`)
        .then((res) => {
          this.$message.success(this.$t("hr.paiementSupprime"));
          // Reload data
          this.fetchEmployeePayments();
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.errorCode
          ) {
            this.$message.error(
              this.$t(`errorCode.${err.response.data.errorCode}`)
            );
          } else {
            this.$message.error(
              this.$t("error.supp")
            );
          }
        });
    },
    async handlePreview(file) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj);
      }
      this.previewImage = file.url || file.preview;
      this.previewVisible = true;
    },
    handleChangeImage({ fileList }) {
      this.fileList = fileList;
    },
    handleChangeSelect1(value) {},
    handleChangeSelect2(value) {},
    handleBlur() {},
    handleFocus() {},
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
  },
};
</script>

<style>
@import "../../compV3/common.css";
.highlight {
  background-color: rgb(255, 192, 105);
  padding: 0px;
}
.editable-row-operations {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.editable-row-operations a {
  margin-right: 0;
  margin-bottom: 4px;
}

.editable-row-operations a:last-child {
  margin-bottom: 0;
}

/* Statistics Cards */
.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-right: 16px;
  flex-shrink: 0;
}

.total-payments .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.total-amount .stat-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.total-loans .stat-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.total-employees .stat-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #262626;
}

/* Filter Item Layout - Labels on top */
.filter-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #595959;
  margin-bottom: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Table Container */
.table-container {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .filters-section .ant-col {
    margin-bottom: 16px;
  }
}

.gap-2 {
  gap: 8px;
}

.mb-4 {
  margin-bottom: 24px;
}

.mb-3 {
  margin-bottom: 16px;
}

.text-muted {
  color: #8c8c8c;
}

.text-primary {
  color: #1890ff;
}

.text-warning {
  color: #faad14;
}

.small {
  font-size: 12px;
}

.bg-light {
  background-color: #fafafa;
}

.rounded {
  border-radius: 8px;
}

.p-3 {
  padding: 16px;
}

.mr-1 {
  margin-right: 4px;
}

.mr-2 {
  margin-right: 8px;
}

.d-block {
  display: block;
}

/* Employee Info Display */
.employee-info-display {
  margin-top: 8px;
}

.info-item {
  margin-bottom: 8px;
}

.info-item strong {
  margin-right: 8px;
  color: #595959;
}

.info-item span {
  color: #262626;
}

.attendance-summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.attendance-ref-card {
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  border: 1px solid #d6e4ff;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(24, 144, 255, 0.06);
}

.attendance-ref-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.attendance-ref-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.attendance-ref-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #e6f4ff;
  color: #1677ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.attendance-ref-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f1f1f;
  line-height: 1.3;
}

.attendance-ref-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
}

.attendance-ref-empty {
  margin-top: 12px;
  color: #8c8c8c;
  font-size: 13px;
}

.att-stat {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px 8px;
  text-align: center;
}

.att-label {
  font-size: 10px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.att-value {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.attendance-details-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
  color: #595959;
}

.session-cell .session-main {
  font-weight: 500;
  color: #262626;
}

.session-cell .session-class {
  color: #595959;
  font-weight: 400;
}

.session-cell .session-time {
  font-size: 12px;
  margin-top: 2px;
}

.ml-3 {
  margin-left: 16px;
}

@media (max-width: 992px) {
  .attendance-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 576px) {
  .attendance-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .attendance-ref-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
